"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { ArrowRight, Layers, SlidersHorizontal, Cylinder, Filter, Disc, Grid } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface JumpCardItem {
  id: string;
  targetAnchor: string;
  name: Record<Lang, string>;
  category: Record<Lang, string>;
  description: Record<Lang, string>;
  imageSrc: string;
  imageAlt: Record<Lang, string>;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const componentCards: JumpCardItem[] = [{
  id: "flat-panel",
  targetAnchor: "#flat-panel",
  name: {
    en: "Flat Panels",
    zh: "平板筛板",
    es: "Paneles planos",
    de: "Flachsiebböden",
    fr: "Panneaux plans",
    pt: "Painéis planos"
  },
  category: {
    en: "Rigid Decks",
    zh: "高刚性筛板",
    es: "Cribas rígidas",
    de: "Starre Siebböden",
    fr: "Tamis rigides",
    pt: "Telas rígidas"
  },
  description: {
    en: "Heavy-duty flat wedge wire panels framed for vibratory decks, coal screening, and static cross-flow dewatering.",
    zh: "重型加强框平整楔形丝筛板，用于振动筛分机、煤炭分选与静态错流脱水系统。",
    es: "Paneles planos reforzados para cribas vibrantes, cribado de carbón y deshidratación estática.",
    de: "Schwere Flachspaltsiebe mit Verstärkungsrahmen für Schwingsiebe, Kohleaufbereitung und statische Entwässerung.",
    fr: "Panneaux plans renforcés pour tables vibrantes, criblage minier et égouttage statique.",
    pt: "Painéis planos reforçados para peneiras vibratórias, separação mineral e desaguamento estático."
  },
  imageSrc: "https://mutualfilter.com/wp-content/uploads/2026/09/Flat-screen.jpg?v=8koqnxz00tf",
  imageAlt: {
    en: "Heavy duty flat wedge wire screen panel with rectangular support framing",
    zh: "带矩形加强支撑框架的重型平整楔形丝筛板",
    es: "Panel plano de alambre de cuña con bastidor de soporte rectangular",
    de: "Flachspaltsieb mit rechteckigem Verstärkungsrahmen",
    fr: "Panneau plan en fil triangulaire avec cadre de support rectangulaire",
    pt: "Painel plano de arame em cunha com estrutura de suporte retangular"
  },
  icon: Grid
}, {
  id: "curved-screens",
  targetAnchor: "#curved-screens",
  name: {
    en: "Curved Screens",
    zh: "弧形筛面",
    es: "Tamices curvos",
    de: "Bogensiebe",
    fr: "Grilles courbes",
    pt: "Telas curvas"
  },
  category: {
    en: "Curved Geometry",
    zh: "弧面分级",
    es: "Geometría curva",
    de: "Bogengeometrie",
    fr: "Géométrie courbe",
    pt: "Geometria curva"
  },
  description: {
    en: "Formed radial curved screen decks for gravity-assisted acceleration, slurry screening, and starch extraction.",
    zh: "依据半径精确滚弯成型的弧形筛网，利用重力与离心加速度实现矿浆精细筛分与淀粉提取。",
    es: "Tamices radiales curvos conformados para aceleración por gravedad, pulpas minerales y extracción de almidón.",
    de: "Präzise gerollte Bogensiebeinsätze für schwerkraftunterstützte Feststoffabtrennung und Stärkeextraktion.",
    fr: "Grilles courbes radiales pour accélération gravitaire, séparation de barbotines et amidonnerie.",
    pt: "Telas curvas radiais para aceleração por gravidade, polpas minerais e extração de amido."
  },
  imageSrc: "https://mutualfilter.com/wp-content/uploads/2026/09/Flat-screen.jpg?v=8koqnxz00tf",
  imageAlt: {
    en: "Precision curved radial wedge wire screen surface",
    zh: "精密滚弯径向弧形楔形丝筛网表面",
    es: "Superficie de tamiz curvo radial de alambre en cuña",
    de: "Präzise gebogenes radiales Bogensiebelement",
    fr: "Surface de grille courbe radiale en fil triangulaire",
    pt: "Superfície de tela curva radial de arame cunha"
  },
  icon: Disc
}, {
  id: "cylindrical-screens",
  targetAnchor: "#cylindrical-screens",
  name: {
    en: "Cylindrical Screens",
    zh: "圆筒滤网 (滚筒与转筛)",
    es: "Cilindros filtrantes",
    de: "Spaltsiebrohre & Zylinder",
    fr: "Tamis cylindriques",
    pt: "Telas cilíndricas"
  },
  category: {
    en: "Axial & Circumferential",
    zh: "轴向与环向全覆盖",
    es: "Axial y circunferencial",
    de: "Axial & Umlaufend",
    fr: "Axial et circonférentiel",
    pt: "Axial e circunferencial"
  },
  description: {
    en: "Continuously welded rotary cylinders covering internal axial slot scraper profiles and external circumferential drum variants.",
    zh: "全焊接回转圆筒，完整覆盖内部平滑轴向缝隙刮刀式滤筒与外向环向缝隙转筒筛两种结构形态。",
    es: "Cilindros rotativos electrosoldados continuos con ranura axial interna para rascador o ranura circunferencial externa.",
    de: "Vollverschweißte Drehtrommelzylinder, umfasst axiale Innenspalte für Schaber und umlaufende Außenspalte für Trommeln.",
    fr: "Cylindres rotatifs soudés en continu couvrant les profils axiaux internes pour racleur et les versions circonférentielles.",
    pt: "Cilindros rotativos soldados contínuos cobrindo variantes com fenda axial interna para raspador e fenda circunferencial externa."
  },
  imageSrc: "https://mutualfilter.com/wp-content/uploads/2026/09/Wedge-Wire-Screen-Cylinders-and-Wedge-Wire-Tubes.webp?v=e9avljt14rt",
  imageAlt: {
    en: "Precision rotary wedge wire screen cylinders and drum screens",
    zh: "精密旋转约翰逊楔形网圆筒与滚筒筛组件",
    es: "Cilindros y tambores rotativos de rejilla de alambre de cuña",
    de: "Präzise Spaltsiebrohr-Zylinder und Trommelsiebe",
    fr: "Cylindres et tambours rotatifs en fil triangulaire",
    pt: "Cilindros e tambores rotativos de tela de arame perfilado"
  },
  icon: Cylinder
}, {
  id: "screen-tubes",
  targetAnchor: "#screen-tubes",
  name: {
    en: "Screen Tubes & Pipes",
    zh: "筛管与滤水管 (含管线与复合滤芯)",
    es: "Tubos y tuberías filtrantes",
    de: "Spaltsiebrohre & Filterrohre",
    fr: "Tubes crépines et conduites",
    pt: "Tubos e condutos filtrantes"
  },
  category: {
    en: "Pipes & Candle Tubes",
    zh: "深井管与压力滤芯",
    es: "Bujías y tuberías",
    de: "Kerzen & Rohre",
    fr: "Tubes et bougies",
    pt: "Tubos e velas"
  },
  description: {
    en: "High-collapse tubular filter candles and intake pipes covering heavy-wall screen pipes and precision wedge wire candle tubes.",
    zh: "高抗瘪压管状滤芯与取水管总成，完整覆盖重型筛管（Screen Pipes）与精密楔形丝烛式滤芯（Wedge Wire Tubes）。",
    es: "Bujías filtrantes tubulares y tuberías de captación de alto colapso que abarcan tuberías reforzadas y tubos de alambre en cuña.",
    de: "Kollapsfeste Filterkerzen und Brunnenfilterrohre, umfasst dickwandige Siebrohre und hochpräzise Spaltsiebkerzen.",
    fr: "Bougies tubulaires haute résistance à l'écrasement et crépines de forage couvrant conduites lourdes et tubes profilés.",
    pt: "Velas tubulares de alto colapso e tubos de captação cobrindo tubos de parede grossa e elementos de arame perfilado."
  },
  imageSrc: "https://mutualfilter.com/wp-content/uploads/2026/09/wedge-wire-screen-tube.webp?v=6p3tywtelm2",
  imageAlt: {
    en: "High collapse continuous slot wedge wire screen tubes and filter candles",
    zh: "高抗压连续缝隙楔形丝筛管与烛式滤芯总成",
    es: "Tubos filtrantes y bujías de alambre en cuña de alto colapso",
    de: "Kollapsfeste Spaltsiebrohre und Filterkerzen",
    fr: "Tubes crépines haute résistance et bougies filtrantes",
    pt: "Tubos de tela cunha de alto colapso e velas filtrantes"
  },
  icon: SlidersHorizontal
}, {
  id: "filter-nozzles",
  targetAnchor: "#filter-nozzles",
  name: {
    en: "Filter Nozzles & Strainers",
    zh: "滤水帽与配水支管",
    es: "Boquillas y toberas filtrantes",
    de: "Filterdüsen & Siebkörbe",
    fr: "Buses et crépines de filtration",
    pt: "Crepinas e bicos filtrantes"
  },
  category: {
    en: "Distribution & Retention",
    zh: "配水与介质截留",
    es: "Distribución y retención",
    de: "Verteilung & Rückhalt",
    fr: "Distribution & rétention",
    pt: "Distribuição e retenção"
  },
  description: {
    en: "Threaded dome and cylinder nozzles designed for uniform liquid distribution and resin retention in underdrain floors.",
    zh: "螺纹连接穹顶及圆柱型滤水帽，专为离子交换滤池底部配水板的均匀流体分布与树脂介质截留而设计。",
    es: "Toberas roscadas cilíndricas y en cúpula para distribución uniforme y retención de resina en fondos falsos.",
    de: "Gewindedüsen mit Kappe oder Zylinderkörper für gleichmäßige Wasserverteilung und Harzrückhalt in Filterböden.",
    fr: "Buses filetées en dôme et cylindriques pour distribution uniforme et rétention de résine sur faux-fonds.",
    pt: "Crepinas roscadas em cúpula e cilindro para distribuição uniforme de água e retenção de resina em fundos falsos."
  },
  imageSrc: "https://mutualfilter.com/wp-content/uploads/2026/11/photobank-800-800.webp?v=zyvsltczbzs",
  imageAlt: {
    en: "Stainless steel wedge wire filter nozzles and resin retention strainers",
    zh: "不锈钢楔形丝滤水帽与离子交换树脂截留过滤器",
    es: "Boquillas filtrantes de alambre de cuña e interceptores de resina",
    de: "Edelstahl-Filterdüsen und Harzfänger aus Spaltsieb",
    fr: "Buses de filtration et crépines de rétention de résine en inox",
    pt: "Crepinas filtrantes em arame cunha e retentores de resina em aço inox"
  },
  icon: Filter
}, {
  id: "sieve-bend",
  targetAnchor: "#sieve-bend",
  name: {
    en: "Sieve Bend Screens",
    zh: "弧形脱水筛板 (DSM 筛)",
    es: "Cribas curvas DSM",
    de: "Spaltsiebböden (DSM-Siebe)",
    fr: "Grilles d'essorage DSM",
    pt: "Telas estáticas DSM"
  },
  category: {
    en: "Cross-Flow Dewatering",
    zh: "错流重力脱水",
    es: "Deshidratación estática",
    de: "Querstrom-Entwässerung",
    fr: "Égouttage transversal",
    pt: "Desaguamento estático"
  },
  description: {
    en: "Stationary curved sieve bends engineered for high-volume cross-flow dewatering, fiber separation, and slurry classification.",
    zh: "静态弧形筛板（DSM 筛），专为大通量重力错流脱水、细纤维高效分离与矿浆精细分级而设计。",
    es: "Cribas curvas estáticas tipo DSM para deshidratación estática de gran caudal, separación de fibras y clasificación.",
    de: "Statische Bogensiebe (DSM-Typ) für hochvolumige Querstromentwässerung, Faserrückgewinnung und Schlammklassierung.",
    fr: "Grilles d'essorage courbes statiques DSM pour débits élevés, séparation de fibres et classification de pulpes.",
    pt: "Telas curvas estáticas tipo DSM para desaguamento de alto volume, separação de fibras e classificação mineral."
  },
  imageSrc: "https://mutualfilter.com/wp-content/uploads/2026/09/Flat-screen.jpg?v=8koqnxz00tf",
  imageAlt: {
    en: "Industrial cross flow curved sieve bend screen panel",
    zh: "工业重力错流静态弧形脱水筛板",
    es: "Panel de criba curva DSM para deshidratación por flujo transversal",
    de: "Industrieller Spaltsiebboden für Querstromentwässerung",
    fr: "Panneau de grille courbe DSM pour égouttage transversal industriel",
    pt: "Painel de tela curva DSM para desaguamento por fluxo cruzado"
  },
  icon: Layers
}];
const jumpCopy = {
  eyebrow: {
    en: "Product Families",
    zh: "产品分类概览",
    es: "Familias de producto",
    de: "Produktfamilien",
    fr: "Familles de produits",
    pt: "Famílias de produtos"
  },
  h2: {
    en: "Wedge Wire Screen Configurations",
    zh: "约翰逊楔形滤网结构分类",
    es: "Configuraciones de rejillas de cuña",
    de: "Spaltsieb-Konfigurationen",
    fr: "Configurations de grilles en fil triangulaire",
    pt: "Configurações de telas de arame cunha"
  },
  lead: {
    en: "Explore continuous-slot profile wire assemblies fabricated in flat, curved, tubular, cylindrical, and nozzle formats for mechanical separation.",
    zh: "浏览涵盖平板、弧面、管状、回转圆筒及滤水帽的全系连续缝隙型线焊接组件，满足严苛工业机械固液分离要求。",
    es: "Explore conjuntos de alambre perfilado con ranura continua en formatos plano, curvo, tubular, cilíndrico y toberas.",
    de: "Entdecken Sie Spaltsiebbaugruppen mit Endlosspalt in flacher, gebogener, rohrförmiger, zylindrischer und Düsenbauform.",
    fr: "Découvrez les ensembles en fil profilé à fente continue en versions planes, courbes, tubulaires, cylindriques et buses.",
    pt: "Explore conjuntos de arame perfilado em formatos plano, curvo, tubular, cilíndrico e crepinas para separação mecânica."
  }
};
export default function WedgeWire() {
  const {
    lang
  } = useLanguage();
  return <section id="wedgewirecategories" data-nav="light" className="bg-muted/50 mf-section border-b border-border">
      <div className="mf-container">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-10">
            <div className="max-w-2xl space-y-3">
              <div className="inline-flex items-center gap-2">
                <Badge variant="outline" className="bg-card text-foreground px-2.5 py-1 mf-caption font-semibold uppercase tracking-wider font-mono">
                  {pick(lang, jumpCopy.eyebrow)}
                </Badge>
              </div>
              <h2 className="mf-h2 text-foreground font-default">
                {pick(lang, jumpCopy.h2)}
              </h2>
              <p className="mf-lead text-muted-foreground max-w-[65ch]">
                {pick(lang, jumpCopy.lead)}
              </p>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {componentCards.map((item, index) => {
          const IconComponent = item.icon;
          const currentTitle = pick(lang, item.name);
          return <Reveal key={item.id} delay={Math.min(index * 60, 300)} className="h-full">
                <Link to={item.targetAnchor} className="group block h-full rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  <Card className="mf-card-hover h-full flex flex-col justify-between border-border bg-card overflow-hidden gap-0 py-0">
                    <div>
                      <div className="relative mf-ratio-card w-full mf-card-media mf-img-zoom">
                        <img src={item.imageSrc} alt={pick(lang, item.imageAlt)} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                        <div className="absolute top-3 left-3 bg-surface-dark text-surface-dark-foreground px-2.5 py-1 rounded-md border border-surface-dark-border flex items-center gap-1.5 mf-caption font-medium">
                          <IconComponent className="size-4 text-accent" />
                          <span>{pick(lang, item.category)}</span>
                        </div>
                      </div>

                      <CardHeader className="p-4 sm:p-5 pb-2">
                        <CardTitle className="mf-card-title mf-h3 text-card-foreground">
                          {currentTitle}
                        </CardTitle>
                      </CardHeader>

                      <CardContent className="p-4 sm:p-5 pt-0 pb-4">
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {pick(lang, item.description)}
                        </p>
                      </CardContent>
                    </div>

                    <CardFooter className="p-4 sm:p-5 pt-0 border-t border-border mt-auto">
                      <div className="w-full pt-3 flex items-center justify-between">
                        <span className="text-sm font-semibold text-primary">
                          {currentTitle}
                        </span>
                        <div aria-hidden="true" className="size-8 rounded-md bg-muted text-primary border border-border flex items-center justify-center mf-arrow-nudge">
                          <ArrowRight className="size-4" />
                        </div>
                      </div>
                    </CardFooter>
                  </Card>
                </Link>
              </Reveal>;
        })}
        </div>
      </div>
    </section>;
}